import {ChangeDetectionStrategy, Component, computed, contentChildren, input} from '@angular/core';
import {DateTime} from 'luxon';
import {AssertionUtils} from '../../common/utils/assertion-utils';
import {ShiftInstance} from './shift-instance';
import {ShiftComponent} from './shift.component';

@Component({
  selector: 'vdw-shift-schedule',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShiftScheduleComponent {
  public days = input.required<readonly number[]>();
  private shiftsQuery = contentChildren(ShiftComponent, {descendants: true});

  private refDate: DateTime;
  private shifts = computed<ShiftComponent[]>(() => this.sortShifts(this.shiftsQuery() as ShiftComponent[]));

  public getShiftsForPeriod(start: Date, end: Date): ShiftInstance[] {
    if (AssertionUtils.isEmpty(this.days()) || AssertionUtils.isEmpty(this.shifts())) {
      return [];
    }

    const startDate = DateTime.fromJSDate(start);
    const endDate = DateTime.fromJSDate(end);
    this.refDate = startDate.startOf('day').minus({days: 1});

    const result: ShiftInstance[] = [];
    while (this.refDate < endDate) {
      for (const shift of this.shifts()) {
        this.addShiftInstancesForDate(shift, result, startDate);
      }

      this.refDate = this.refDate.plus({days: 1});
    }

    if (!AssertionUtils.isEmpty(result) && result[result.length - 1].end < endDate) {
      this.addEmptySpace(result, endDate, startDate);
    }

    result?.forEach((instance: ShiftInstance) => instance.fit(startDate, endDate));
    return result?.filter((instance: ShiftInstance) => instance.durationInMinutes > 0);
  }

  public getShiftsForDay(date: Date): ShiftInstance[] {
    return this.getShiftsForPeriod(DateTime.fromJSDate(date).startOf('day').toJSDate(), DateTime.fromJSDate(date).endOf('day').toJSDate());
  }

  private addShiftInstancesForDate(shift: ShiftComponent, result: ShiftInstance[], startDate: DateTime): void {
    while (!this.days().includes(this.refDate.weekday)) {
      this.refDate = this.refDate.plus({days: 1});
    }

    let shiftStart = shift.getStartOnDate(this.refDate);

    this.addEmptySpace(result, shiftStart, startDate);

    let shiftEnd = shift.getEndOnDate(this.refDate);

    if (shiftEnd < shiftStart) {
      shiftEnd = shiftEnd.plus({days: 1});
    }
    if (shiftStart < startDate) {
      shiftStart = startDate;
    }
    if (shiftEnd <= startDate) {
      return;
    }

    result.push(new ShiftInstance(shiftStart, shiftEnd, shift.name()));
  }

  private addEmptySpace(result: ShiftInstance[], shiftStart: DateTime, startDate: DateTime): void {
    const previousIterEnd = result.length > 0 ? result[result.length - 1].end : startDate.startOf('day').minus({days: 1});
    const emptySpaceDuration = shiftStart.diff(previousIterEnd);
    if (emptySpaceDuration.as('minutes') < 0) {
      throw new Error('Overlapping shifts are not allowed!');
    }

    if (emptySpaceDuration.as('minutes') === 0 || shiftStart <= startDate) {
      return;
    }

    result.push(new ShiftInstance(previousIterEnd, shiftStart));
  }

  private sortShifts(shifts: ShiftComponent[]): ShiftComponent[] {
    return shifts.sort((shiftA: ShiftComponent, shiftB: ShiftComponent) => {
      const [hoursA, minutesA] = shiftA.getTimeAsNumeric(shiftA.start());
      const [hoursB, minutesB] = shiftB.getTimeAsNumeric(shiftB.start());

      return hoursA === hoursB ? minutesA - minutesB : hoursA - hoursB;
    });
  }
}
